import { generateUUID } from '@/lib/utils';
import {
  Cell,
  CellViewOptions,
  InvalidCell,
  Pipeline,
  RecordsCell,
  RecordsLikeCell,
  Sort,
  SqlCell,
  VariableCell,
  Visualisation,
} from '@/explore/types';

import { setViewOptions, removeViewOptions, setViewOption } from './view-options';

export * from './cells';
export * from './chat-cell';
export * from './conversation-cell';

export * from './variable-cell';
export * from './view-options';

export const isRecordsCell = (cell: Cell): cell is RecordsCell => cell.kind === 'records';

export const isSqlCell = (cell: Cell): cell is SqlCell => cell.kind === 'sql';

export const isVariableCell = (cell: Cell): cell is VariableCell => cell.kind === 'variable';

// Returns true for any cell that results with table like data in its query.
export const isRecordsLikeCell = (cell: Cell): cell is RecordsLikeCell =>
  isRecordsCell(cell) || isSqlCell(cell);

export type CellWithTitle = Extract<Cell, { title?: string | null }>;
export function isCellWithTitle(cell: Cell): cell is CellWithTitle {
  return 'title' in cell;
}

export type CellWithPipeline = Extract<Cell, { pipeline?: Pipeline }>;
export function isCellWithPipeline(cell: Cell): cell is CellWithPipeline {
  return 'pipeline' in cell;
}

export type CellWithVisualisations = Extract<Cell, { visualisations?: Visualisation[] }>;
export function isCellWithVisualisations(cell: Cell): cell is CellWithVisualisations {
  return 'visualisations' in cell;
}

export type CellWithSort = Extract<Cell, { sort?: Sort }>;

export type ReplicableCell = RecordsLikeCell;
export const isReplicableCell = (cell: Cell): cell is ReplicableCell => isRecordsLikeCell(cell);

export const isInvalidCell = (cell: Cell): cell is InvalidCell => cell.kind === 'invalid';

export const restoreInvalidCell = (cell: Cell): Exclude<Cell, InvalidCell> => {
  if (isInvalidCell(cell)) {
    return setViewOptions(cell.cell, cell.viewOptions ?? {});
  }
  return cell;
};

export const generateCellId = generateUUID;

export function setCellTitle(cell: CellWithTitle, title: string): CellWithTitle {
  return { ...cell, title };
}

export function setCellPipeline(cell: CellWithPipeline, pipeline: Pipeline): CellWithPipeline {
  return ensureValidCellKind({ ...cell, pipeline });
}

export function setCellVisualisations(
  cell: CellWithVisualisations,
  visualisations: Visualisation[],
): CellWithVisualisations {
  return { ...cell, visualisations };
}

export function setCellSort(cell: CellWithSort, sort?: Sort): CellWithSort {
  return { ...cell, sort };
}

function ensureValidCellKind(cell: CellWithPipeline): CellWithPipeline {
  const firstOperation = cell.pipeline.operations.at(0);
  if (firstOperation === undefined) {
    return cell;
  }
  switch (firstOperation.operation) {
    case 'funnel':
      return { ...cell, kind: 'funnel' };
    case 'cohort':
      return { ...cell, kind: 'cohort' };
    case 'sql':
      return { ...cell, kind: 'sql' };
    case 'python':
      return { ...cell, kind: 'python' };
    default:
      return cell;
  }
}

export const setCellHeight = (cell: Cell, height: number | undefined): Cell => {
  return setViewOption(cell, 'height', height);
};

export const resetCellRollback = (cell: Cell): Cell => {
  const options = cell.viewOptions as CellViewOptions | undefined;
  if (options?.rollback !== undefined) {
    let updatedCell = cell;
    if (isRecordsLikeCell(cell) && options.rollback.originalVisualisations !== undefined) {
      updatedCell = { ...cell, visualisations: options.rollback.originalVisualisations };
    }
    return removeViewOptions(updatedCell, ['rollback']);
  }
  return cell;
};
